import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { LIKE } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import TextInput from "../../framework/TextInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import NumberInput from "../../framework/NumberInput";
import {
  searchManufacturer,
  searchManufacturerGroup,
} from "../../services/manufacturer-services";
import SelectInput from "../../framework/SelectInput";
const ManufacturerReportFilter = ({
  reportRef,
  onFilterShow,
  applyFilterOnMount = true,
  refreshData = false,
}) => {
  const [manufacturerName, setManufacturerName] = useState("");
  const [manufacturerGroupName, setManufacturerGroupName] = useState("");
  const [manufacturerShortCode, setManufacturerShortCode] = useState("");
  const [limit, setLimit] = useState(100);
  const [orderBy] = useState([{ fieldName: "updatedTime", orderType: "desc" }]);
  const [manufacturerSuggestions, setManufacturerSuggestions] = useState([]);
  const [manufacturerGroupSuggestions, setManufacturerGroupSuggestions] =
    useState([]);
  const manufacturerNameRef = useRef();
  const manufactureGroupNameRef = useRef();
  const applyFilterRef = useRef();
  const filterLimitRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};

      if (!isEmpty(manufacturerName)) {
        filters.push(
          createFilterParam(
            "manufacturerName",
            manufacturerName,
            "string",
            LIKE
          )
        );
      }
      if (!isEmpty(manufacturerGroupName)) {
        filters.push(
          createFilterParam(
            "manufactureGroupName",
            manufacturerGroupName,
            "string",
            LIKE
          )
        );
      }
      if (!isEmpty(manufacturerShortCode)) {
        filters.push(
          createFilterParam(
            "manufacturerShortCode",
            manufacturerShortCode,
            "string",
            LIKE
          )
        );
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }
      if (orderBy) {
        result.orderBy = orderBy;
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;
      reportRef.current?.applyFilters(result);
    },
    [
      manufacturerName,
      manufacturerGroupName,
      manufacturerShortCode,
      limit,
      orderBy,
      reportRef,
    ]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let debounce = useCallback(
    _.debounce(() => {
      applyFilterHandler();
    }, 300),
    []
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      return applyFilterRef.current.click();
    }
  }, []);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="amr_filter_manufacturer_name"
            label="Manufacturer Name"
            ref={manufacturerNameRef}
            options={manufacturerSuggestions}
            getOptionLabel={(option) => option.label || option}
            isOptionEqualToValue={(option, value) => {
              return value.id === "custom"
                ? value
                : option.label === value.label;
            }}
            disableClearable={false}
            noOptionsText="Search Manufacturer Name"
            onFocus={() => setCurrentComponent("amr_filter_manufacturer_name")}
            onChange={(e, values) => {
              setManufacturerName(values ? values.label : "");
            }}
            value={manufacturerName}
            onInputChange={async (e) => {
              if (!e) return;
              setManufacturerName(e.target.value);
              setManufacturerSuggestions([]);
              if (!e?.target?.value) setManufacturerName(null);
              if (e?.target?.value?.length > 2) {
                searchManufacturer(
                  `manufacturerName=${e.target.value.trim()}`
                ).then((resp) => {
                  let updatedResp = resp?.result?.rows?.map((manufacturer) => ({
                    id: manufacturer.manufacturerId,
                    label: manufacturer.manufacturerName,
                  }));
                  setManufacturerSuggestions(updatedResp);
                });
              }
            }}
            markers={JSON.stringify({
              up: "amr_filter_manufacturer_name",
              down: "amr_filter_manufacturer_grp_name",
              right: "amr_filter_manufacturer_grp_name",
              left: "amr_filter_manufacturer_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="amr_filter_manufacturer_grp_name"
            label="Manufacturer Group"
            ref={manufactureGroupNameRef}
            options={manufacturerGroupSuggestions}
            getOptionLabel={(option) => option?.name || option}
            disableClearable={false}
            noOptionsText="Search Manufacturer Group"
            onFocus={() =>
              setCurrentComponent("amr_filter_manufacturer_grp_name")
            }
            onChange={(e, values) => {
              setManufacturerGroupName(values?.name);
            }}
            value={manufacturerGroupName}
            onInputChange={async (e, value) => {
              const newValue = e.target.value;
              setManufacturerGroupName(e.target.value);
              if (newValue?.length >= 2) {
                const payload = {
                  filters: [
                    {
                      fieldName: "manufactureGroupName",
                      fieldType: { type: "string", operation: "like" },
                      fieldValue: newValue,
                    },
                  ],
                };
                const resp = await searchManufacturerGroup(payload);
                setManufacturerGroupSuggestions(resp);
              }
            }}
            markers={JSON.stringify({
              up: "amr_filter_manufacturer_name",
              down: "amr_filter_manufacturer_srt_name",
              right: "amr_filter_manufacturer_srt_name",
              left: "amr_filter_manufacturer_name",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="amr_filter_manufacturer_srt_name"
            label="Short Code"
            onFocus={() =>
              setCurrentComponent("amr_filter_manufacturer_srt_name")
            }
            value={manufacturerShortCode}
            onChange={(e) => {
              setManufacturerShortCode(e.target.value);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "amr_filter_manufacturer_grp_name",
                down: "add_limit_filter",
                right: "add_limit_filter",
                left: "amr_filter_manufacturer_grp_name",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="add_limit_filter"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("dsr_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "amr_filter_manufacturer_srt_name",
                down: "amr_apply_filter_button",
                left: "amr_filter_manufacturer_srt_name",
                right: "amr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="amr_apply_filter_button"
            label="Apply"
            ref={applyFilterRef}
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => {
              applyFilterHandler(true);
            }}
            markers={JSON.stringify({
              up: "add_limit_filter",
              left: "add_limit_filter",
              right: "amr_apply_filter_button",
              down: "amr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      manufacturerNameRef?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);
  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);
  useEffect(() => {
    if (applyFilterOnMount) debounce();
    return () => {
      debounce.cancel();
    };
  }, [applyFilterOnMount, debounce]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default ManufacturerReportFilter;
